<script setup lang="ts">
import type {ArraySchema, ArrayValue, ObjectSchema, Schema, SchemaValue} from '@/generated-report/interfaces';
import FormField from "@/generated-report/components/form-schema/FormField.vue";
import FieldDescription from "@/generated-report/components/form-schema/fields/FieldDescription.vue";
import {Button} from "@/core/components/ui/button";
import {Plus, Trash2} from "@lucide/vue";

const {schema, nestingLevel = 1} = defineProps<{
    schema: ArraySchema;
    nestingLevel?: number;
}>();

const model = defineModel<ArrayValue>({required: false});

/**
 * Create a default value based on the schema type
 */
const createDefaultValue = (fieldSchema: Schema): SchemaValue => {
    switch (fieldSchema.type) {
        case 'string':
            return '';
        case 'array':
            return [];
        case 'object': {
            const objectSchema = fieldSchema as ObjectSchema;
            const defaultObject: Record<string, SchemaValue> = {};
            for (const [key, propertySchema] of Object.entries(objectSchema.properties)) {
                defaultObject[key] = createDefaultValue(propertySchema);
            }

            return defaultObject;
        }
        default:
            return undefined;
    }
};

/**
 * Update item at index
 */
const updateItem = (index: number, newValue: SchemaValue) => {
    if (!model.value) {
        return;
    }

    const newArrayValue: ArrayValue = [...model.value];
    newArrayValue[index] = newValue;

    model.value = newArrayValue;
};

/**
 * Add a new item to the array
 */
const addItem = () => {
    const defaultValue = createDefaultValue(schema.items);

    if (!model.value) {
        model.value = [defaultValue];
    } else {
        model.value = [...model.value, defaultValue];
    }
};

/**
 * Remove an item from the array
 */
const removeItem = (index: number) => {
    if (!model.value) {
        return;
    }

    model.value = model.value.filter((_, i) => i !== index);
};
</script>

<template>
  <div class="space-y-4">
    <div class="flex justify-between items-center">
      <FieldDescription v-if="schema.description" :nesting-level="nestingLevel">
        {{ schema.description }}
      </FieldDescription>
      <Button
        variant="outline"
        size="sm"
        @click="addItem"
      >
        <Plus class="h-4 w-4" />
        Ajouter
      </Button>
    </div>

    <div>
      <div v-if="!model || !model.length" class="text-muted-foreground text-sm">
        Aucun élément.
      </div>
      <div v-else class="space-y-4">
        <div
          v-for="(item, index) in model"
          :key="index"
          class="group flex items-center gap-2"
        >
          <div class="flex-1 min-w-0">
            <FormField
              :schema="schema.items"
              :model-value="item"
              :nesting-level="nestingLevel + 1"
              @update:model-value="updateItem(index, $event)"
            />
          </div>
          <Button
            variant="ghost"
            size="icon"
            class="overflow-hidden w-0 p-0 m-0 translate-x-2 transition-all ease-in-out flex-shrink-0
             group-hover:opacity-100 group-hover:w-10 group-hover:p-2 group-hover:m-0 group-hover:translate-x-0"
            @click="removeItem(index)"
          >
            <Trash2 class="h-4 w-4 text-red-500" />
          </Button>
        </div>
      </div>
    </div>
  </div>
</template>
